import React, { useMemo, useState } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Typography, 
  Space, 
  Button, 
  Tag, 
  Progress, 
  Spin,
  Empty,
  Statistic,
  Select
} from 'antd';
import {
  TrophyOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  PlayCircleOutlined,
  FileTextOutlined,
  CalendarOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useResponsive } from '../hooks/useResponsive';
import { useExamAvailability } from '../hooks/user';
import { useExamUserRetakes } from '../hooks/exam';
import authService from '../services/authService';

const { Title, Text } = Typography;

interface ExamResult {
  id: number;
  user_id: number;
  exam_id: number;
  score: number;
  total_score: number;
  passed: number;
  taken_at: string;
}

interface ExamDetail {
  id: number;
  course_id?: number;
  title: string;
  description?: string;
  total_marks?: number;
  pass_marks?: number;
  duration_minutes?: number;
  is_active?: number;
  exam_type_id?: number;
  created_at?: string;
  exam_results?: ExamResult | null;
  // Alternative property names that might come from API
  totalMarks?: number;
  passMarks?: number;
  duration?: number;
  isActive?: boolean;
  examResults?: ExamResult | null;
}

const Exams: React.FC = () => {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const { data: examData, isLoading, error } = useExamAvailability();
  const currentUser = authService.getCurrentUser();
  const userId = currentUser?.id?.toString();
  const [selectedTopic, setSelectedTopic] = useState<string | undefined>(undefined);
  const [selectedLM, setSelectedLM] = useState<string | undefined>(undefined);

  // Helper functions to safely get exam properties
  const getExamProperty = (exam: any, ...propertyNames: string[]) => {
    if (!exam) return null;
    for (const propName of propertyNames) {
      // Check direct properties first
      if (exam[propName] !== undefined && exam[propName] !== null && exam[propName] !== '') {
        return exam[propName];
      }
      // Check nested exam_details if it exists
      if (exam.exam_details && exam.exam_details[propName] !== undefined && exam.exam_details[propName] !== null && exam.exam_details[propName] !== '') {
        return exam.exam_details[propName];
      }
    }
    return null;
  };
  
  const getExamTitle = (exam: any) => {
    if (!exam) return 'Unknown Exam';
    // Check exam_details.title first, then fallback to other options
    const title = exam?.exam_details?.title || getExamProperty(exam, 'title', 'name', 'exam_title', 'exam_name');
    return title || `Exam ${exam?.exam_id || exam?.id || 'Unknown'}`;
  };
  
  const getExamDescription = (exam: any) => {
    if (!exam) return 'No description available';
    // Check exam_details.description first
    const desc = exam?.exam_details?.description || getExamProperty(exam, 'description', 'exam_description', 'details');
    return desc || 'No description available';
  };
  
  const getTotalMarks = (exam: any) => {
    if (!exam) return 100;
    // Check exam_details.total_marks first
    const marks = exam?.exam_details?.total_marks || getExamProperty(exam, 'total_marks', 'totalMarks', 'total_score', 'max_marks');
    return Number(marks) || 100; // Default to 100 if not found
  };
  
  const getPassMarks = (exam: any) => {
    if (!exam) return 60;
    // Check exam_details.pass_marks first
    const passMarks = exam?.exam_details?.pass_marks || getExamProperty(exam, 'pass_marks', 'passMarks', 'passing_marks', 'minimum_marks');
    return Number(passMarks) || Math.ceil(getTotalMarks(exam) * 0.6); // Default to 60% of total
  };
  
  const getDuration = (exam: any) => {
    if (!exam) return 60;
    // Check exam_details.duration_minutes first
    const duration = exam?.exam_details?.duration_minutes || getExamProperty(exam, 'duration_minutes', 'duration', 'time_limit', 'time_allowed');
    return Number(duration) || 60; // Default to 60 minutes
  };
  
  const getIsActive = (exam: any) => {
    if (!exam) return false;
    // Check exam_details.is_active first
    const active = exam?.exam_details?.is_active || getExamProperty(exam, 'is_active', 'isActive', 'active', 'status');
    return active === 1 || active === true || active === 'active' || active === 'available';
  };
  
  const getExamResults = (exam: any) => {
    if (!exam) return null;
    // exam_results is an array, so get the first result if it exists
    if (exam.exam_results && Array.isArray(exam.exam_results) && exam.exam_results.length > 0) {
      return exam.exam_results[0];
    }
    // Fallback to other possible property names
    const results = getExamProperty(exam, 'examResults', 'results', 'user_result');
    if (results && Array.isArray(results) && results.length > 0) {
      return results[0];
    }
    return results;
  };

  // Exam titles follow the "<Topic> <LM>: description" format, e.g. "EQ LM02: ..."
  const getExamTopicAndLM = (exam: any) => {
    const prefix = getExamTitle(exam).split(':')[0].trim();
    const [topic, lm] = prefix.split(/\s+/);
    // Normalize case/whitespace so filter selections match regardless of title formatting
    return { topic: topic ? topic.trim().toUpperCase() : '', lm: lm ? lm.trim().toUpperCase() : '' };
  };

  // Get exam IDs for fetching retake status
  const examIds = useMemo(() => {
    let rawExams = examData;
    if (examData && examData.data && !examData.id) {
      rawExams = examData.data;
    }
    let examsList: any[] = [];
    if (Array.isArray(rawExams)) {
      examsList = rawExams.filter(exam => exam != null);
    } else if (rawExams && typeof rawExams === 'object') {
      examsList = [rawExams];
    }
    return examsList.map(exam => {
      const id = exam?.exam_details?.id || exam?.exam_id || exam?.id;
      return id?.toString() || '';
    }).filter(id => id !== '');
  }, [examData]);

  // Topic/LM options for the filter dropdowns, derived from exam titles
  const { topicOptions, lmOptions } = useMemo(() => {
    let rawExams = examData;
    if (examData && examData.data && !examData.id) {
      rawExams = examData.data;
    }
    let examsList: any[] = [];
    if (Array.isArray(rawExams)) {
      examsList = rawExams.filter(exam => exam != null);
    } else if (rawExams && typeof rawExams === 'object') {
      examsList = [rawExams];
    }

    const topicSet = new Set<string>();
    examsList.forEach(exam => {
      const { topic } = getExamTopicAndLM(exam);
      if (topic) topicSet.add(topic);
    });

    const examsForLM = selectedTopic
      ? examsList.filter(exam => getExamTopicAndLM(exam).topic === selectedTopic)
      : examsList;
    const lmSet = new Set<string>();
    examsForLM.forEach(exam => {
      const { lm } = getExamTopicAndLM(exam);
      if (lm) lmSet.add(lm);
    });

    return {
      topicOptions: Array.from(topicSet).map(topic => ({ value: topic, label: topic })),
      lmOptions: Array.from(lmSet).map(lm => ({ value: lm, label: lm })),
    };
  }, [examData, selectedTopic]);


  // Fetch retake status for all exams
  const retakeQueries = useExamUserRetakes(examIds, userId);

  // Create a map of examId to retake status
  const retakeStatusMap = useMemo(() => {
    const map: { [key: string]: boolean } = {};
    examIds.forEach((examId, index) => {
      const retakeData = retakeQueries[index]?.data;
      map[examId] = retakeData?.is_retake === true;
    });
    return map;
  }, [examIds, retakeQueries]);

  console.log('Exams page - exam data:', examData);
  console.log('Type of examData:', typeof examData);
  console.log('Is array:', Array.isArray(examData));
  console.log('First exam:', examData && (Array.isArray(examData) ? examData[0] : examData));

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    console.error('Error loading exams:', error);
  }

  // Handle exam data (could be array or single object, or nested in data property)
  let rawExams = examData;
  
  // If the data is wrapped in a data property, extract it
  if (examData && examData.data && !examData.id) {
    rawExams = examData.data;
  }
  
  // Handle various possible data structures and filter out null/undefined items
  let examsList: any[] = [];
  
  if (Array.isArray(rawExams)) {
    examsList = rawExams.filter(exam => exam != null);
  } else if (rawExams && typeof rawExams === 'object') {
    examsList = [rawExams];
  }

  const exams: any[] = examsList;

  console.log('Processed exams array:', exams);
  console.log('Number of exams:', exams.length);
  console.log('Valid exams (non-null):', exams.filter(exam => exam != null).length);

  // Apply the Topic/LM filters selected above
  const filteredExams = exams.filter(exam => {
    const { topic, lm } = getExamTopicAndLM(exam);
    if (selectedTopic && topic !== selectedTopic) return false;
    if (selectedLM && lm !== selectedLM) return false;
    return true;
  });

  const handleStartExam = (exam: any) => {
    const examId = exam?.exam_details?.id || exam?.exam_id || exam?.id;
    navigate(`/exam/${examId}`, { 
      state: { 
        examId: examId,
        examTitle: getExamTitle(exam),
        examDescription: getExamDescription(exam),
        totalMarks: getTotalMarks(exam),
        passMarks: getPassMarks(exam),
        duration: getDuration(exam),
        isActive: getIsActive(exam)
      } 
    });
  };

  const handleViewResults = (exam: any) => {
    const examResults = getExamResults(exam);
    if (examResults) {
      const resultData = {
        examTitle: getExamTitle(exam),
        totalQuestions: getTotalMarks(exam),
        correctAnswers: examResults.score,
        score: examResults.score,
        passed: examResults.passed === 1,
        userAnswers: {},
        examData: {
          passMarks: getPassMarks(exam),
          totalMarks: getTotalMarks(exam)
        }
      };
      
      const examId = exam?.exam_details?.id || exam?.exam_id || exam?.id;
      navigate(`/exam/${examId}/results`, { state: resultData });
    }
  };
  

  const getExamStatusConfig = (exam: any) => {
    const examResults = getExamResults(exam);
    if (examResults) {
      const passed = examResults.passed === 1;
      return {
        status: passed ? 'completed-pass' : 'completed-fail', 
        color: passed ? '#52c41a' : '#ff4d4f',
        bgColor: passed ? '#f6ffed' : '#fff2f0',
        borderColor: passed ? '#b7eb8f' : '#ffccc7',
        icon: passed ? '🏆' : '📚',
        text: passed ? 'PASSED' : 'FAILED',
        buttonText: 'View Results'
      };
    } else if (getIsActive(exam)) {
      return {
        status: 'available',
        color: '#1890ff',
        bgColor: '#f0f8ff',
        borderColor: '#91d5ff',
        icon: '📝',
        text: 'AVAILABLE',
        buttonText: 'Start Exam'
      };
    } else {
      return {
        status: 'inactive',
        color: '#8c8c8c',
        bgColor: '#fafafa',
        borderColor: '#d9d9d9',
        icon: '🔒',
        text: 'INACTIVE',
        buttonText: 'Not Available'
      };
    }
  };

  if (exams.length === 0) {
    return (
      <div style={{ 
        maxWidth: '1200px',
        margin: '0 auto',
        padding: isMobile ? '16px' : '24px'
      }}>
        <Title level={2} style={{ marginBottom: '24px' }}>
          Exams
        </Title>
        
        {/* Show debug info if we have data but no exams */}
        {examData && process.env.NODE_ENV === 'development' && (
          <div style={{ 
            marginBottom: '24px', 
            padding: '16px', 
            backgroundColor: '#fff2e8', 
            borderRadius: '8px',
            border: '1px solid #ffbb96'
          }}>
            <Text strong style={{ color: '#d46b08' }}>Debug Info:</Text>
            <div style={{ marginTop: '8px', fontSize: '12px', fontFamily: 'monospace' }}>
              <div>Raw data type: {typeof examData}</div>
              <div>Is array: {Array.isArray(examData).toString()}</div>
              <div>Has data property: {examData?.data ? 'yes' : 'no'}</div>
              <div>Keys: {examData ? Object.keys(examData).join(', ') : 'none'}</div>
            </div>
          </div>
        )}
        
        <Empty 
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={examData ? "No exams found in response data" : "No exams available"}
        />
      </div>
    );
  }

  return (
    <div style={{ 
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '16px' : '24px'
    }}>
      <div style={{ marginBottom: '32px' }}>
        <Title level={2} style={{ margin: 0, marginBottom: '8px' }}>
          Exams
        </Title>
        <Text type="secondary" style={{ fontSize: '16px' }}>
          View and take available exams
        </Text>
      </div>

      {/* Debug Information */}
      {/* {process.env.NODE_ENV === 'development' && examData && (
        <div style={{ 
          marginBottom: '24px', 
          padding: '16px', 
          backgroundColor: '#f5f5f5', 
          borderRadius: '8px',
          fontSize: '12px',
          fontFamily: 'monospace'
        }}>
          <strong>Debug - Raw Exam Data:</strong>
          <pre>{JSON.stringify(examData, null, 2)}</pre>
        </div>
      )} */}

      {/* Filter Controls */}
      <div style={{
        marginBottom: '24px',
        padding: '16px',
        background: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #e9ecef'
      }}>
        <Text strong style={{
          fontSize: isMobile ? '14px' : '16px',
          color: '#262626',
          display: 'block',
          marginBottom: '12px'
        }}>
          Filter by Topic and LM
        </Text>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: '12px'
        }}>
          <div>
            <Text style={{
              fontSize: isMobile ? '12px' : '14px',
              color: '#666',
              display: 'block',
              marginBottom: '4px'
            }}>
              Select Topic:
            </Text>
            <Select
              style={{ width: '100%' }}
              placeholder="All Topics"
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
              }
              value={selectedTopic}
              onChange={(value) => {
                setSelectedTopic(value);
                setSelectedLM(undefined); // Reset LM when topic changes
              }}
              options={topicOptions}
            />
          </div>

          <div>
            <Text style={{
              fontSize: isMobile ? '12px' : '14px',
              color: '#666',
              display: 'block',
              marginBottom: '4px'
            }}>
              Select LM:
            </Text>
            <Select
              style={{ width: '100%' }}
              placeholder="All LMs"
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
              }
              value={selectedLM}
              onChange={setSelectedLM}
              options={lmOptions}
            />
          </div>
        </div>
      </div>

      {filteredExams.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No exams match the selected filters"
        />
      ) : (
      <Row gutter={[24, 24]}>
        {filteredExams.map((exam, index) => {
          console.log(`Rendering exam ${index}:`, exam);
          console.log('Exam properties:', Object.keys(exam || {}));
          console.log('Exam details:', exam?.exam_details);
          console.log('Exam results:', exam?.exam_results);
          console.log('getExamResults returns:', getExamResults(exam));
          const statusConfig = getExamStatusConfig(exam);
          
          // Use the availability record's own id first - exam_details.id repeats across records for the same exam
          const examId = exam?.id ?? exam?.exam_details?.id ?? exam?.exam_id ?? index;
          // Retake queries are keyed by the canonical exam id (exam_details.id first)
          const retakeExamId = (exam?.exam_details?.id || exam?.exam_id || exam?.id || '').toString();
          
          return (
            <Col key={examId} xs={24} sm={12} lg={8}>
              <Card
                style={{
                  borderRadius: '16px',
                  border: `2px solid ${statusConfig.borderColor}`,
                  backgroundColor: statusConfig.bgColor,
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                bodyStyle={{ padding: isMobile ? '16px' : '20px' }}
              >
                {/* Status Badge */}
                <div style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  zIndex: 1
                }}>
                  <Tag 
                    color={statusConfig.color}
                    style={{ 
                      fontWeight: '600',
                      fontSize: '10px',
                      border: 'none'
                    }}
                  >
                    {statusConfig.text}
                  </Tag>
                </div>

                {/* Exam Icon */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    width: isMobile ? '60px' : '80px',
                    height: isMobile ? '60px' : '80px',
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${statusConfig.color}22 0%, ${statusConfig.color}44 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: isMobile ? '28px' : '36px'
                  }}>
                    {statusConfig.icon}
                  </div>
                </div>

                {/* Exam Title */}
                <Title level={4} style={{ 
                  textAlign: 'center',
                  margin: '0 0 8px 0',
                  fontSize: isMobile ? '16px' : '18px',
                  color: '#262626'
                }}>
                  {getExamTitle(exam)}
                </Title>

                {/* Exam Description */}
                <Text style={{
                  display: 'block',
                  textAlign: 'center',
                  color: '#8c8c8c',
                  fontSize: isMobile ? '12px' : '14px',
                  marginBottom: '16px',
                  lineHeight: '1.4'
                }}>
                  {getExamDescription(exam)}
                </Text>

                {/* Exam Details */}
                <div style={{ marginBottom: '20px' }}>
                  <Row gutter={[8, 8]}>
                    <Col xs={12}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ 
                          color: statusConfig.color, 
                          fontSize: isMobile ? '18px' : '20px',
                          fontWeight: 'bold'
                        }}>
                          {getTotalMarks(exam)}
                        </div>
                        <Text style={{ 
                          fontSize: isMobile ? '10px' : '11px',
                          color: '#8c8c8c'
                        }}>
                          Total Marks
                        </Text>
                      </div>
                    </Col>
                    
                    <Col xs={12}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ 
                          color: statusConfig.color, 
                          fontSize: isMobile ? '18px' : '20px',
                          fontWeight: 'bold'
                        }}>
                          {getDuration(exam)}
                        </div>
                        <Text style={{ 
                          fontSize: isMobile ? '10px' : '11px',
                          color: '#8c8c8c'
                        }}>
                          Duration
                        </Text>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* Exam Results (if available) */}
                {getExamResults(exam) && (
                  <div style={{ 
                    marginBottom: '20px',
                    padding: '12px',
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    borderRadius: '8px',
                    border: `1px solid ${statusConfig.borderColor}`
                  }}>
                    <div style={{ textAlign: 'center', marginBottom: '8px' }}>
                      <Text strong style={{ color: statusConfig.color }}>
                        Your Score: {getExamResults(exam)?.score || 0}/{getExamResults(exam)?.total_score || 0}
                      </Text>
                    </div>
                    <Progress
                      percent={(getExamResults(exam)?.score || 0) / (getExamResults(exam)?.total_score || 1) * 100}
                      strokeColor={statusConfig.color}
                      trailColor="#f0f0f0"
                      strokeWidth={6}
                      showInfo={false}
                    />
                    <div style={{ textAlign: 'center', marginTop: '4px' }}>
                      <Text style={{ 
                        fontSize: '11px',
                        color: '#8c8c8c'
                      }}>
                        Taken: {getExamResults(exam)?.taken_at ? new Date(getExamResults(exam).taken_at).toLocaleDateString() : 'N/A'}
                      </Text>
                    </div>
                  </div>
                )}
                {/* Action Button */}
                <Button
                  type={getExamResults(exam) ? 'default' : 'primary'}
                  size="large"
                  icon={getExamResults(exam) ? <FileTextOutlined /> : <PlayCircleOutlined />}
                  onClick={() => getExamResults(exam) ? handleViewResults(exam) : handleStartExam(exam)}
                  disabled={!getIsActive(exam) && !getExamResults(exam)}
                  style={{
                    width: '100%',
                    height: isMobile ? '40px' : '44px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    backgroundColor: getExamResults(exam) ? undefined : statusConfig.color,
                    borderColor: getExamResults(exam) ? statusConfig.color : statusConfig.color
                  }}
                >
                  {statusConfig.buttonText}
                </Button>

                {/* Retake Button - shown when user has completed exam and retake is allowed */}
                {getExamResults(exam) && retakeStatusMap[retakeExamId] && (
                  <Button
                    type="primary"
                    size="large"
                    icon={<ReloadOutlined />}
                    onClick={() => handleStartExam(exam)}
                    style={{
                      width: '100%',
                      height: isMobile ? '40px' : '44px',
                      borderRadius: '8px',
                      fontWeight: '600',
                      marginTop: '8px',
                      backgroundColor: '#faad14',
                      borderColor: '#faad14'
                    }}
                  >
                    Retake Exam
                  </Button>
                )}

                {/* Pass/Fail Mark Requirements */}
                <div style={{ 
                  marginTop: '12px',
                  textAlign: 'center'
                }}>
                  <Text style={{ 
                    fontSize: '11px',
                    color: '#8c8c8c'
                  }}>
                    Pass Mark: {getPassMarks(exam)}/{getTotalMarks(exam)}
                  </Text>
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>
      )}
    </div>
  );
};

export default Exams;